import React, { useState, useEffect } from 'react';
import { getProducts, createProduct, updateProduct, deleteProduct } from '../../services/api';
import { PencilIcon, TrashIcon, PlusIcon, CubeIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import AdminLayout from './AdminLayout';

const ProductManagement = () => {
  const [products, setProducts]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showModal, setShowModal]       = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData]         = useState({ name:'', description:'', price:'', category:'', image:null });
  const [preview, setPreview]           = useState(null);
  const [submitting, setSubmitting]     = useState(false);

  useEffect(() => { loadProducts(); }, []);

  const loadProducts = () =>
    getProducts()
      .then(r => setProducts(r.data.products))
      .catch(() => toast.error('Failed to load products'))
      .finally(() => setLoading(false));

  const handleInputChange  = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleImageChange  = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFormData({ ...formData, image: file });
    setPreview(URL.createObjectURL(file));
  };

  const openAdd = () => {
    setEditingProduct(null);
    setFormData({ name:'', description:'', price:'', category:'', image:null });
    setPreview(null);
    setShowModal(true);
  };

  const handleEdit = (p) => {
    setEditingProduct(p);
    setFormData({ name:p.name, description:p.description||'', price:p.price, category:p.category||'', image:null });
    setPreview(p.image_url || null);
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setPreview(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const form = new FormData();
    Object.keys(formData).forEach(k => { if (formData[k] !== null && formData[k] !== '') form.append(k, formData[k]); });
    try {
      if (editingProduct) {
        await updateProduct(editingProduct.id, form);
        toast.success('Product updated!');
      } else {
        await createProduct(form);
        toast.success('Product added!');
      }
      closeModal();
      loadProducts();
    } catch {
      toast.error(editingProduct ? 'Update failed' : 'Add failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try { await deleteProduct(id); toast.success('Deleted'); loadProducts(); }
    catch { toast.error('Delete failed'); }
  };

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
    *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'Nunito',sans-serif; }

    .pm-page-title { font-size:1.6rem; font-weight:900; color:#111827; margin-bottom:0.25rem; }
    .pm-page-sub   { font-size:0.88rem; color:#64748b; font-weight:600; }

    .pm-header {
      display:flex; align-items:center; justify-content:space-between;
      margin-bottom:2rem; flex-wrap:wrap; gap:1rem;
    }

    .pm-add-btn {
      display:flex; align-items:center; gap:0.5rem;
      background:#059669; color:white; border:none;
      padding:0.65rem 1.25rem; border-radius:0.875rem;
      font-weight:800; font-size:0.9rem; cursor:pointer;
      font-family:'Nunito',sans-serif; transition:all 0.2s;
      box-shadow:0 4px 12px rgba(5,150,105,0.25);
    }
    .pm-add-btn:hover { background:#047857; transform:translateY(-2px); box-shadow:0 6px 16px rgba(5,150,105,0.35); }
    .pm-add-btn svg { width:1rem; height:1rem; }

    .pm-table-wrap {
      background:white; border-radius:1.5rem;
      border:1px solid #f1f5f9; box-shadow:0 4px 16px rgba(0,0,0,0.04);
      overflow:hidden;
    }

    .pm-empty {
      display:flex; flex-direction:column; align-items:center;
      justify-content:center; padding:4rem; color:#94a3b8;
    }
    .pm-empty svg { width:3rem; height:3rem; margin-bottom:0.75rem; }
    .pm-empty p   { font-weight:700; font-size:1rem; }

    .pm-table { width:100%; border-collapse:collapse; }
    .pm-th {
      background:#f8fafc; padding:0.85rem 1.25rem;
      text-align:left; font-size:0.78rem; font-weight:800;
      color:#64748b; text-transform:uppercase; letter-spacing:0.05em;
      border-bottom:1px solid #f1f5f9;
    }
    .pm-tr { transition:background 0.15s; }
    .pm-tr:hover { background:#f8fafc; }
    .pm-tr:not(:last-child) td { border-bottom:1px solid #f1f5f9; }
    .pm-td {
      padding:1rem 1.25rem; font-size:0.88rem;
      font-weight:700; color:#374151; vertical-align:middle;
    }

    .pm-action-btn {
      width:34px; height:34px; border-radius:0.6rem; border:1.5px solid;
      display:inline-flex; align-items:center; justify-content:center;
      cursor:pointer; transition:all 0.2s; margin-right:0.4rem;
    }
    .pm-action-btn svg { width:0.9rem; height:0.9rem; }
    .pm-edit-btn { background:#eff6ff; color:#2563eb; border-color:#bfdbfe; }
    .pm-edit-btn:hover { background:#2563eb; color:white; transform:scale(1.08); }
    .pm-del-btn  { background:#fff1f2; color:#dc2626; border-color:#fecdd3; }
    .pm-del-btn:hover { background:#dc2626; color:white; transform:scale(1.08); }

    /* ── Modal Overlay ── */
    .pm-modal-bg {
      position:fixed; inset:0; background:rgba(0,0,0,0.4);
      backdrop-filter:blur(4px); display:flex;
      align-items:center; justify-content:center; z-index:999; padding:1rem;
    }

    /* ── Modal Box: fixed height with flex layout so footer always shows ── */
    .pm-modal {
      background:white; border-radius:1.75rem; width:100%;
      max-width:500px;
      height:auto;
      max-height:88vh;          /* never taller than viewport */
      display:flex;
      flex-direction:column;    /* header | scrollable body | footer */
      box-shadow:0 24px 60px rgba(0,0,0,0.18);
      animation:modalIn 0.25s ease;
      overflow:hidden;          /* clip children to rounded corners */
    }
    @keyframes modalIn {
      from { opacity:0; transform:scale(0.94) translateY(16px); }
      to   { opacity:1; transform:scale(1)    translateY(0);    }
    }

    /* ── Header: fixed, never scrolls ── */
    .pm-modal-header {
      flex-shrink:0;
      display:flex; align-items:center; justify-content:space-between;
      padding:1.4rem 1.75rem; border-bottom:1px solid #f1f5f9;
    }
    .pm-modal-header h3 { font-size:1.15rem; font-weight:900; color:#111827; }
    .pm-modal-header button {
      width:32px; height:32px; border-radius:0.6rem; border:1.5px solid #e5e7eb;
      background:#f8fafc; color:#64748b; font-size:1rem; cursor:pointer;
      display:flex; align-items:center; justify-content:center;
      font-family:'Nunito',sans-serif; transition:all 0.2s;
    }
    .pm-modal-header button:hover { background:#fee2e2; color:#dc2626; border-color:#fecdd3; }

    /* ── Body: scrollable, fills remaining space ── */
    .pm-modal-body {
      flex:1;
      overflow-y:auto;          /* ← THIS enables internal scroll */
      padding:1.5rem 1.75rem;
      display:flex; flex-direction:column; gap:1rem;
    }
    /* nice custom scrollbar */
    .pm-modal-body::-webkit-scrollbar { width:5px; }
    .pm-modal-body::-webkit-scrollbar-track { background:#f8fafc; }
    .pm-modal-body::-webkit-scrollbar-thumb { background:#d1d5db; border-radius:99px; }

    /* ── Footer: fixed, never scrolls ── */
    .pm-modal-footer {
      flex-shrink:0;
      display:flex; gap:0.75rem; padding:1.25rem 1.75rem;
      border-top:1px solid #f1f5f9; justify-content:flex-end;
      background:white;
    }
    .pm-modal-footer button {
      padding:0.65rem 1.5rem; border-radius:0.875rem; font-weight:800;
      font-size:0.9rem; cursor:pointer; font-family:'Nunito',sans-serif;
      transition:all 0.2s;
    }
    .pm-modal-footer button[type="button"] {
      background:#f1f5f9; color:#64748b; border:1.5px solid #e5e7eb;
    }
    .pm-modal-footer button[type="button"]:hover { background:#e2e8f0; }
    .pm-modal-footer button[type="submit"] {
      background:#059669; color:white; border:none;
      box-shadow:0 4px 12px rgba(5,150,105,0.25); min-width:130px;
    }
    .pm-modal-footer button[type="submit"]:hover { background:#047857; transform:translateY(-1px); }
    .pm-modal-footer button:disabled { opacity:0.6; cursor:not-allowed; transform:none !important; }

    /* ── Form Fields ── */
    .pm-field { display:flex; flex-direction:column; gap:0.4rem; }
    .pm-field label { font-size:0.8rem; font-weight:800; color:#374151; }

    .pm-input, .pm-textarea {
      padding:0.65rem 0.9rem; border-radius:0.75rem;
      border:1.5px solid #e5e7eb; font-family:'Nunito',sans-serif;
      font-size:0.88rem; font-weight:600; color:#111827;
      outline:none; transition:border-color 0.2s, box-shadow 0.2s;
      background:#fafafa; width:100%;
    }
    .pm-input:focus, .pm-textarea:focus {
      border-color:#059669; background:white;
      box-shadow:0 0 0 3px rgba(5,150,105,0.12);
    }
    .pm-textarea { resize:vertical; min-height:90px; }

    /* ── Image Upload Zone ── */
    .pm-img-upload-zone {
      border:2px dashed #d1d5db; border-radius:1rem;
      padding:1.25rem; text-align:center; cursor:pointer;
      transition:all 0.2s; background:#fafafa; position:relative;
    }
    .pm-img-upload-zone:hover { border-color:#059669; background:#f0fdf4; }
    .pm-img-upload-zone input[type="file"] {
      position:absolute; inset:0; opacity:0; cursor:pointer; width:100%; height:100%;
    }
    .pm-img-upload-icon { font-size:2rem; margin-bottom:0.3rem; }
    .pm-img-upload-text { font-size:0.82rem; font-weight:700; color:#64748b; }
    .pm-img-upload-sub  { font-size:0.74rem; color:#94a3b8; margin-top:0.2rem; }

    /* ── Image Preview ── */
    .pm-preview-wrap {
      position:relative; border-radius:1rem; overflow:hidden;
      border:1.5px solid #e5e7eb; margin-top:0.5rem;
    }
    .pm-preview-img  { width:100%; max-height:180px; object-fit:cover; display:block; }
    .pm-preview-change {
      position:absolute; inset:0; background:rgba(0,0,0,0.45);
      display:flex; align-items:center; justify-content:center;
      opacity:0; transition:opacity 0.2s; cursor:pointer;
    }
    .pm-preview-wrap:hover .pm-preview-change { opacity:1; }
    .pm-preview-change span {
      color:white; font-weight:800; font-size:0.82rem;
      background:rgba(0,0,0,0.5); padding:0.35rem 0.85rem; border-radius:9999px;
    }
    .pm-preview-change input[type="file"] {
      position:absolute; inset:0; opacity:0; cursor:pointer; width:100%; height:100%;
    }
  `;

  if (loading) return (
    <AdminLayout>
      <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'60vh' }}>
        <div style={{ width:'3rem', height:'3rem', border:'3px solid #f3f4f6', borderTopColor:'#059669', borderRadius:'50%', animation:'spin 0.7s linear infinite' }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout>
      <style>{styles}</style>

      <div className="pm-header">
        <div>
          <div className="pm-page-title">Product Management</div>
          <div className="pm-page-sub">{products.length} products in catalogue</div>
        </div>
        <button className="pm-add-btn" onClick={openAdd}>
          <PlusIcon /> Add Product
        </button>
      </div>

      <div className="pm-table-wrap">
        {products.length === 0 ? (
          <div className="pm-empty">
            <CubeIcon />
            <p>No products yet</p>
          </div>
        ) : (
          <table className="pm-table">
            <thead>
              <tr>
                <th className="pm-th">Image</th>
                <th className="pm-th">Product</th>
                <th className="pm-th">Category</th>
                <th className="pm-th">Price</th>
                <th className="pm-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id} className="pm-tr">
                  <td className="pm-td">
                    {p.image_url ? (
                      <img
                        src={p.image_url}
                        alt={p.name}
                        style={{ width:52, height:52, borderRadius:10, objectFit:'cover', border:'1.5px solid #f1f5f9' }}
                        onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                      />
                    ) : null}
                    <div style={{
                      width:52, height:52, background:'#f1f5f9', borderRadius:10,
                      alignItems:'center', justifyContent:'center',
                      display: p.image_url ? 'none' : 'flex'
                    }}>
                      <CubeIcon style={{ width:22, height:22, color:'#94a3b8' }} />
                    </div>
                  </td>
                  <td className="pm-td" style={{ fontWeight:800 }}>{p.name}</td>
                  <td className="pm-td">
                    <span style={{ background:'#f0fdf4', color:'#059669', fontWeight:700, fontSize:'0.75rem', padding:'0.2rem 0.65rem', borderRadius:9999 }}>
                      {p.category || '—'}
                    </span>
                  </td>
                  <td className="pm-td" style={{ color:'#059669', fontWeight:800 }}>₹{Number(p.price).toFixed(2)}</td>
                  <td className="pm-td">
                    <button className="pm-action-btn pm-edit-btn" onClick={() => handleEdit(p)} title="Edit">
                      <PencilIcon />
                    </button>
                    <button className="pm-action-btn pm-del-btn" onClick={() => handleDelete(p.id)} title="Delete">
                      <TrashIcon />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Modal ── */}
      {showModal && (
        <div className="pm-modal-bg" onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="pm-modal">

            {/* Fixed Header */}
            <div className="pm-modal-header">
              <h3>{editingProduct ? 'Edit Product' : 'Add Product'}</h3>
              <button onClick={closeModal}>✕</button>
            </div>

            <form onSubmit={handleSubmit} style={{ display:'contents' }}>

              {/* Scrollable Body */}
              <div className="pm-modal-body">

                {/* Image Upload */}
                <div className="pm-field">
                  <label>Product Image</label>
                  {!preview ? (
                    <div className="pm-img-upload-zone">
                      <input type="file" accept="image/*" onChange={handleImageChange} />
                      <div className="pm-img-upload-icon">🖼️</div>
                      <div className="pm-img-upload-text">Click to upload image</div>
                      <div className="pm-img-upload-sub">PNG, JPG, WEBP up to 5MB</div>
                    </div>
                  ) : (
                    <div className="pm-preview-wrap">
                      <img src={preview} alt="preview" className="pm-preview-img" />
                      <div className="pm-preview-change">
                        <input type="file" accept="image/*" onChange={handleImageChange} />
                        <span>🔄 Change Image</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="pm-field">
                  <label>Product Name *</label>
                  <input className="pm-input" name="name" value={formData.name} onChange={handleInputChange} required placeholder="e.g. Fresh Milk 500ml" />
                </div>

                <div className="pm-field">
                  <label>Category</label>
                  <input className="pm-input" name="category" value={formData.category} onChange={handleInputChange} placeholder="e.g. Dairy, Snacks" />
                </div>

                <div className="pm-field">
                  <label>Price (₹) *</label>
                  <input className="pm-input" type="number" step="0.01" min="0" name="price" value={formData.price} onChange={handleInputChange} required placeholder="0.00" />
                </div>

                <div className="pm-field">
                  <label>Description</label>
                  <textarea className="pm-textarea" name="description" value={formData.description} onChange={handleInputChange} placeholder="Short product description..." />
                </div>

              </div>

              {/* Fixed Footer — always visible */}
              <div className="pm-modal-footer">
                <button type="button" onClick={closeModal} disabled={submitting}>Cancel</button>
                <button type="submit" disabled={submitting}>
                  {submitting ? '⏳ Saving...' : (editingProduct ? '✅ Update Product' : '💾 Save Product')}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

    </AdminLayout>
  );
};

export default ProductManagement;